import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, inject } from '@angular/core'
import { Control } from '@grapecity/wijmo'
import { FlexGrid } from '@grapecity/wijmo.grid'

import { UtilityService } from './utility.service'
import { ViewportService } from './viewport.service'

@Directive({
    selector: '[appAutoSize]',
    host: {
        '(window:resize)': 'onWindowResize($event)',
    },
    standalone: true,
})
export class AutoSizeDirective implements AfterViewInit, OnDestroy {
    private utility = inject(UtilityService)
    private viewport = inject(ViewportService)
    private el = inject(ElementRef)

    constructor() {
        this.htmlItem = this.el.nativeElement
        this.wjControl = Control.getControl(this.el.nativeElement)

        this.debounceResize = this.utility.debounce(250, () => this.resize())
    }

    ngAfterViewInit() {
        this.layoutRequiresUpdateHandler = this.utility.layoutRequiresUpdate.on(() => {
            setTimeout(() => this.resize(), 0)
        })
        this.viewportChangedHandler = this.viewport.changed.on(() => {
            setTimeout(() => this.resize(), 0)
        })

        if (this.breakpoint != null) {
            this.breakpointWidth = this.utility.getResponsiveBreakpoint(this.breakpoint)
        }
        this.resize()
        setTimeout(() => this.resize(), 100)
    }
    ngOnDestroy() {
        this.utility.layoutRequiresUpdate.off(this.layoutRequiresUpdateHandler)
        this.viewport.changed.off(this.viewportChangedHandler)
        this.teardownMobileObserver()
        if (this.isSingleView) {
            document.documentElement.style.overflowY = 'auto'
        }
    }

    @Input() padding = 15
    @Input() minHeight = 350
    @Input() breakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | null = null
    @Input() breakpointHeight = 350
    @Input() isSingleView = false
    @Input() paddingControl = ''
    @Input() limitAfterMax = false
    @Input() mobileMinHeight = 200

    private breakpointWidth = 0
    private htmlItem: HTMLElement
    private wjControl: Control
    private previousHeight = 0
    private previousWidth = 0
    private previousMobileHeight = ''
    private resizeAttempts = 0
    private debounceResize: () => void
    private layoutRequiresUpdateHandler: any
    private viewportChangedHandler: any
    private layoutObserver: ResizeObserver | null = null
    private desktopFrozenColumns: number | null = null

    private resize() {
        if (!this.wjControl) {
            this.wjControl = Control.getControl(this.htmlItem)
        }
        this.syncMobileObserver()
        this.applyFrozenColumns()
        if (this.viewport.isMobile()) {
            this.resizeMobile()
            return
        }

        this.previousMobileHeight = ''
        this.resizeAttempts++

        const clientRect = this.htmlItem.getBoundingClientRect()
        let top = clientRect.top

        let controlPadding: number | undefined = 0
        if (this.paddingControl != '') {
            const paddingControlElement = document.querySelector(this.paddingControl) as HTMLElement
            if (paddingControlElement) {
                controlPadding = paddingControlElement.offsetHeight
            }
        }
        if (controlPadding === undefined) {
            controlPadding = 0
        }
        this.resizeAttempts = 0
        let height = window.innerHeight - top - this.padding - controlPadding
        const width = window.innerWidth
        let provideUpdate = false

        // if we are currently in a tab, use that instead if we are hidden
        if (top == 0) {
            const parentTab = this.findParentTab(this.htmlItem)
            if (parentTab) {
                top = parentTab.getBoundingClientRect().bottom
                return
            }
        }

        // if view hasn't been initialized...try again
        if (this.resizeAttempts < 3 && top == 0 && clientRect.bottom == 0) {
            this.debounceResize()
            return
        }

        let minHeight = this.minHeight
        let useAutoSize = false

        // if breakpoint in effect, fix our height
        if (this.breakpointWidth != 0 && this.getViewPort().width < this.breakpointWidth) {
            if (height < this.breakpointHeight) {
                minHeight = this.breakpointHeight
                height = this.breakpointHeight
            }

            useAutoSize = this.breakpointHeight == 0
        }

        if (this.previousHeight != height) {
            if (height < minHeight) {
                height = minHeight

                if (this.isSingleView) {
                    document.documentElement.style.overflowY = 'auto'
                }
            } else {
                if (this.isSingleView) {
                    document.documentElement.style.overflowY = 'hidden'
                }
            }

            // set the style via css
            if (useAutoSize || (this.limitAfterMax && this.htmlItem.scrollHeight < height)) {
                this.htmlItem.style.height = 'auto'
                this.htmlItem.style.maxHeight = 'none'
            } else {
                const styleHeight = height.toString() + 'px'
                this.htmlItem.style.height = styleHeight
                this.htmlItem.style.maxHeight = styleHeight
            }

            provideUpdate = true
            this.previousHeight = height
        }

        if (this.previousWidth != width) {
            provideUpdate = true
            this.previousWidth = width
        }

        this.refreshControl(provideUpdate)
    }

    private resizeMobile() {
        const clientRect = this.htmlItem.getBoundingClientRect()
        const scrollY = window.scrollY || document.documentElement.scrollTop || 0
        let layoutTop = clientRect.top + scrollY

        let controlPadding = 0
        if (this.paddingControl != '') {
            const paddingControlElement = document.querySelector(this.paddingControl) as HTMLElement
            if (paddingControlElement) {
                controlPadding = paddingControlElement.offsetHeight
            }
        }

        if (clientRect.top == 0) {
            const parentTab = this.findParentTab(this.htmlItem)
            if (parentTab) {
                return
            }
        }

        if (this.resizeAttempts < 3 && clientRect.top == 0 && clientRect.bottom == 0) {
            this.resizeAttempts++
            this.debounceResize()
            return
        }
        this.resizeAttempts = 0

        const offset = layoutTop + this.padding + controlPadding
        const styleHeight = this.viewport.supportsDvh()
            ? `calc(100dvh - ${offset}px)`
            : `${Math.max(this.mobileMinHeight, window.innerHeight - offset)}px`

        const width = window.innerWidth
        let provideUpdate = false

        if (this.previousMobileHeight != styleHeight) {
            this.htmlItem.style.height = styleHeight
            this.htmlItem.style.maxHeight = styleHeight
            this.previousMobileHeight = styleHeight
            this.previousHeight = 0
            provideUpdate = true
        }

        if (this.previousWidth != width) {
            provideUpdate = true
            this.previousWidth = width
        }

        this.refreshControl(provideUpdate)
    }

    private applyFrozenColumns() {
        const flex = this.wjControl instanceof FlexGrid ? this.wjControl : null
        if (!flex) return

        if (this.viewport.isMobile()) {
            if (flex.frozenColumns !== 0) {
                this.desktopFrozenColumns = flex.frozenColumns
                flex.frozenColumns = 0
            }
            if (flex.cloneFrozenCells !== false) {
                flex.cloneFrozenCells = false
            }
            return
        }

        if (this.desktopFrozenColumns != null && flex.frozenColumns === 0) {
            flex.frozenColumns = this.desktopFrozenColumns
        }
        this.desktopFrozenColumns = flex.frozenColumns
    }

    private refreshControl(provideUpdate: boolean) {
        if (this.wjControl) {
            const flex = this.wjControl instanceof FlexGrid ? this.wjControl : null
            const editing = flex != null && flex.activeEditor != null

            // FlexGrid.refresh/endUpdate while a cell editor is open will dismiss the editor.
            if (!editing && this.wjControl.isUpdating) {
                this.wjControl.endUpdate()
            }

            if (!editing && provideUpdate && this.wjControl.refresh) {
                setTimeout(() => this.wjControl.refresh(), 1)
                this.wjControl.refresh()
            }
        }
    }

    private syncMobileObserver() {
        if (!this.viewport.isMobile()) {
            this.teardownMobileObserver()
            return
        }
        if (this.layoutObserver) return

        this.layoutObserver = new ResizeObserver(() => this.debounceResize())
        let current = this.htmlItem.parentElement
        let target: HTMLElement | null = null
        while (current) {
            if (current.classList.contains('container-fluid')) {
                target = current
                break
            }
            current = current.parentElement
        }
        this.layoutObserver.observe(target ?? this.htmlItem.parentElement ?? this.htmlItem)
    }

    private teardownMobileObserver() {
        this.layoutObserver?.disconnect()
        this.layoutObserver = null
    }

    public onWindowResize() {
        this.debounceResize()
    }
    private findParentTab(element: HTMLElement): HTMLElement | null {
        let current = element.parentElement
        while (current) {
            const navElement = current.querySelector('div .nav')
            if (navElement) {
                return navElement as HTMLElement
            }
            current = current.parentElement
        }
        return null
    }

    private getViewPort() {
        // To get the correct viewport width based on  http://andylangton.co.uk/articles/javascript/get-viewport-size-javascript/
        let e = window as any
        let a = 'inner'

        if (!('innerWidth' in window)) {
            a = 'client'
            e = document.documentElement || document.body
        }

        return {
            width: e[a + 'Width'],
            height: e[a + 'Height'],
        }
    }
}
